import { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import { 
    Search, 
    User as UserIcon, 
    Shield, 
    CheckCircle2, 
    XCircle, 
    Mail, 
    Calendar,
    ArrowLeft,
    RefreshCw,
    LogIn
} from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { useToast } from '../../contexts/ToastContext';

interface User {
    id: string;
    email: string;
    fullName: string;
    pictureUrl: string;
    role: string;
    active: boolean;
    emailVerified: boolean;
    twoFactorEnabled: boolean;
    createdAt: string;
    updatedAt: string;
    salonId: string | null;
}

function UserManagement({ onBack }: { onBack: () => void }) {
    const { addToast } = useToast();
    const [users, setUsers] = useState<User[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [isUpdating, setIsUpdating] = useState<string | null>(null);

    const handleImpersonate = async (user: User) => {
        if (!window.confirm(`¿Iniciar sesión como ${user.fullName} (${user.email})?`)) return;
        
        setIsUpdating(user.id);
        try {
            const data = await adminApi.impersonateUser(user.id);
            
            // Store original admin token to return later
            const adminToken = localStorage.getItem('token');
            if (adminToken) {
                localStorage.setItem('adminToken', adminToken);
            }
            
            // Switch to user token
            localStorage.setItem('token', data.token);
            addToast('success', `Iniciando sesión como ${user.fullName}`);
            
            // Redirect to main app
            window.location.href = '/dashboard';
        } catch (error) {
            addToast('error', 'Error al iniciar suplantación');
        } finally {
            setIsUpdating(null);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async (query?: string) => {
        setIsLoading(true);
        try {
            const data = await adminApi.getUsers(query);
            setUsers(data);
        } catch (error) {
            addToast('error', 'Error al cargar usuarios');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchUsers(searchQuery);
    };

    const toggleUserStatus = async (user: User) => {
        setIsUpdating(user.id);
        try {
            await adminApi.updateUserStatus(user.id, !user.active);
            setUsers(users.map(u => u.id === user.id ? { ...u, active: !u.active } : u));
            addToast('success', `Usuario ${!user.active ? 'activado' : 'desactivado'}`);
        } catch (error) {
            addToast('error', 'Error al actualizar estado');
        } finally {
            setIsUpdating(null);
        }
    };

    const changeUserRole = async (user: User, newRole: string) => {
        if (!window.confirm(`¿Cambiar rol a ${newRole}?`)) return;
        
        setIsUpdating(user.id);
        try {
            await adminApi.updateUserRole(user.id, newRole);
            setUsers(users.map(u => u.id === user.id ? { ...u, role: newRole } : u));
            addToast('success', 'Rol actualizado con éxito');
        } catch (error) {
            addToast('error', 'Error al actualizar rol');
        } finally {
            setIsUpdating(null);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Nav */}
            <div className="flex items-center justify-between">
                <button 
                    onClick={onBack}
                    className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                >
                    <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
                    <span>Volver al Dashboard</span>
                </button>
                <div className="flex items-center gap-2 px-3 py-1 bg-teal-500/10 border border-teal-500/20 rounded-full">
                    <div className="w-1.5 h-1.5 bg-teal-500 rounded-full animate-pulse"></div>
                    <span className="text-[10px] text-teal-500 font-mono tracking-widest uppercase">Users Database</span>
                </div>
            </div>

            {/* Actions Bar */}
            <div className="flex flex-col md:row gap-4 justify-between items-center bg-white/5 p-6 rounded-2xl border border-white/10">
                <form onSubmit={handleSearch} className="relative w-full md:max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                    <Input 
                        placeholder="Buscar por nombre o email..." 
                        className="pl-12 bg-black/40 border-white/10 h-12"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </form>
                <div className="flex gap-2">
                    <Button variant="ghost" onClick={() => fetchUsers()} className="h-12 border border-white/10">
                        <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
                    </Button>
                </div>
            </div>

            {/* Users Table / List */}
            <div className="bg-white/5 border border-white/10 rounded-[32px] overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-white/10 bg-white/10 text-gray-400 text-[10px] uppercase font-bold tracking-widest">
                                <th className="p-6">Usuario</th>
                                <th className="p-6">Rol</th>
                                <th className="p-6">Estado</th>
                                <th className="p-6">Seguridad</th>
                                <th className="p-6">Creado</th>
                                <th className="p-6 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/5">
                            {isLoading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={6} className="p-8 h-20 bg-white/[0.01]"></td>
                                    </tr>
                                ))
                            ) : users.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-20 text-center text-gray-500">
                                        No se encontraron usuarios
                                    </td>
                                </tr>
                            ) : users.map((user) => (
                                <tr key={user.id} className="hover:bg-white/[0.02] transition-colors group">
                                    <td className="p-6">
                                        <div className="flex items-center gap-4">
                                            {user.pictureUrl ? (
                                                <img src={user.pictureUrl} className="w-10 h-10 rounded-xl" alt="" />
                                            ) : (
                                                <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-400">
                                                    <UserIcon size={18} />
                                                </div>
                                            )}
                                            <div>
                                                <p className="font-medium text-sm">{user.fullName}</p>
                                                <p className="text-xs text-gray-500">{user.email}</p>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <span className={`text-[10px] px-2 py-1 rounded-md font-bold tracking-wider ${
                                            user.role === 'SUPER_ADMIN' 
                                            ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' 
                                            : 'bg-blue-500/10 text-blue-400 border border-blue-500/20'
                                        }`}>
                                            {user.role}
                                        </span>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-2">
                                            <div className={`w-2 h-2 rounded-full ${user.active ? 'bg-emerald-500 shadow-[0_0_8px_#10b981]' : 'bg-red-500'}`}></div>
                                            <span className="text-xs">{user.active ? 'Activo' : 'Desactivado'}</span>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-3">
                                            <div className="flex flex-col gap-1">
                                                <div className="flex items-center gap-1.5">
                                                    <Mail size={12} className={user.emailVerified ? 'text-emerald-500' : 'text-gray-600'} />
                                                    <span className="text-[10px] text-gray-500">Email</span>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <Shield size={12} className={user.twoFactorEnabled ? 'text-teal-500' : 'text-gray-600'} />
                                                    <span className="text-[10px] text-gray-500">2FA</span>
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center gap-2 text-gray-500">
                                            <Calendar size={14} />
                                            <span className="text-xs">{new Date(user.createdAt).toLocaleDateString()}</span>
                                        </div>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end gap-2 group-hover:opacity-100 opacity-0 transition-opacity">
                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                className="h-8 text-[10px] text-teal-400 hover:bg-teal-500/10"
                                                onClick={() => handleImpersonate(user)}
                                                disabled={isUpdating === user.id}
                                            >
                                                <LogIn size={14} className="mr-1" />
                                                Entrar
                                            </Button>

                                            <Button 
                                                variant="ghost" 
                                                size="sm"
                                                className={`h-8 text-[10px] ${user.active ? 'text-red-400 hover:bg-red-500/10' : 'text-emerald-400 hover:bg-emerald-500/10'}`}
                                                onClick={() => toggleUserStatus(user)}
                                                isLoading={isUpdating === user.id}
                                            >
                                                {user.active ? <XCircle size={14} className="mr-1" /> : <CheckCircle2 size={14} className="mr-1" />}
                                                {user.active ? 'Desactivar' : 'Activar'}
                                            </Button>
                                            
                                            <select 
                                                className="bg-black/50 border border-white/10 text-[10px] rounded h-8 px-2 focus:outline-none focus:ring-1 focus:ring-teal-500"
                                                value={user.role}
                                                onChange={(e) => changeUserRole(user, e.target.value)}
                                                disabled={isUpdating === user.id}
                                            >
                                                <option value="OWNER">OWNER</option>
                                                <option value="ADMIN">ADMIN</option>
                                                <option value="SUPER_ADMIN">SUPER_ADMIN</option>
                                            </select>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default UserManagement;
